import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Select,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Density,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  CustomerApprovalStatus,
  CustomerDTO,
  CustomerListResponse,
} from "@wmart/shared-types";
import { formatTierWithDiscount } from "@wmart/shared-types";

const STATUS_TONE: Record<CustomerApprovalStatus, Tone> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

/**
 * Truncate a UUID to its first 8 chars + ellipsis. Used as a stand-in for the
 * company-name column — the wire CustomerDTO does not carry a company_name
 * (per `packages/shared-types/src/customer.ts`), and fetching each
 * customer's company on a list view would be N+1 (P08-T11 carry-forward).
 */
function truncateId(id: string | null): string {
  if (!id) return "—";
  return `${id.slice(0, 8)}…`;
}

export function CustomersList() {
  const { notify, notifyProblem } = useToast();
  const [items, setItems] = useState<CustomerDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Default to "approved" — pending applications now live in /applications.
  const [statusFilter, setStatusFilter] = useState<
    CustomerApprovalStatus | "all"
  >("approved");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState<Density>("default");

  // Density: read from localStorage on mount (Q5 carry-forward).
  useEffect(() => {
    const stored = window.localStorage.getItem("wmart.density");
    if (stored === "compact" || stored === "default" || stored === "cosy") {
      setDensity(stored);
    }
  }, []);

  // Debounce the free-text query — mirrors ProductsList.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<CustomerListResponse>(
        `/v1/admin/customers?${params.toString()}`,
      );
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setNextCursor(data.nextCursor);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch on filter/search change.
  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedQ]);

  const hasAnyFilter = statusFilter !== "all" || debouncedQ.trim() !== "";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Input
            type="search"
            label="Search"
            placeholder="Search by email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CustomerApprovalStatus | "all")
            }
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Select>
        </div>
        {hasAnyFilter ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setStatusFilter("all");
              setQ("");
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {items.length === 0 && !loading ? (
        <EmptyState
          icon="user"
          title="No customers match"
          body="Adjust filters to see more results."
        />
      ) : (
        <>
          <Table density={density}>
            <THead>
              <TR>
                <TH>Email</TH>
                <TH>Company</TH>
                <TH>Tier</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((c) => (
                <TR key={c.id}>
                  <TD>
                    <Link
                      to={`/customers/${c.id}`}
                      className="text-ink-900 hover:underline"
                    >
                      {c.email ?? "(no email)"}
                    </Link>
                  </TD>
                  <TD className="font-mono text-body-sm text-ink-500">
                    {truncateId(c.company_id)}
                  </TD>
                  <TD>{formatTierWithDiscount(c.tier)}</TD>
                  <TD>
                    <StatusPill tone={STATUS_TONE[c.approval_status]}>
                      {c.approval_status}
                    </StatusPill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
